import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Smartphone, Copy, Loader2, RefreshCw } from "lucide-react";

export default function PairDevice() {
  const [code, setCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const r = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setRemaining(r);
      if (r === 0) setCode(null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const generate = async () => {
    setLoading(true);
    try {
      // Refresh first so we don't send a stale/revoked access token
      await supabase.auth.refreshSession();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Please sign in again");
      const { data, error } = await supabase.functions.invoke("create-pairing-code");
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      setCode((data as any).code);
      setExpiresAt(new Date((data as any).expires_at).getTime());
    } catch (e: any) {
      toast({ title: "Couldn't generate code", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!code) return;
    await navigator.clipboard.writeText(code);
    toast({ title: "Code copied" });
  };

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div className="min-h-full flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-md cosmic-card p-8 rounded-2xl">
        <div className="flex items-center gap-2 mb-2">
          <Smartphone className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-semibold">Pair a device</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Sign into MindMail on your iPhone home-screen app without Google. Generate a code here,
          then open the PWA on your phone and tap <span className="text-foreground">"I have a pairing code"</span>.
        </p>

        {!code ? (
          <Button onClick={generate} disabled={loading} className="cosmic-button w-full">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Generate pairing code
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="text-center py-6 rounded-xl bg-background/40 border border-border">
              <div className="text-4xl font-mono tracking-[0.4em] font-semibold text-foreground">
                {code}
              </div>
              <div className="text-xs text-muted-foreground mt-3">
                Expires in {mm}:{ss}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={copy} variant="outline" className="flex-1">
                <Copy className="h-4 w-4 mr-2" /> Copy
              </Button>
              <Button onClick={generate} variant="outline" className="flex-1" disabled={loading}>
                <RefreshCw className="h-4 w-4 mr-2" /> New code
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Single use. Anyone with this code can sign in as you until it expires.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
